import { WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { ToolIcon } from '@blocksuite/icons/lit';
import { css, html, nothing, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';

import { aiChatStatusTypography } from '../../styles/ai-chat-typography';

export class ToolCallCard extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    .ai-tool-call-wrapper {
      padding: 6px 10px;
      margin: 4px 0;
      border-radius: 8px;
      border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
      background-color: ${unsafeCSSVarV2('layer/background/primary')};

      .ai-tool-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 6px;
      }

      .ai-icon {
        width: 18px;
        height: 18px;
        flex-shrink: 0;

        svg {
          width: 18px;
          height: 18px;
          display: block;
          flex-shrink: 0;
          color: ${unsafeCSSVarV2('icon/activated')};
        }
      }

      .ai-tool-name {
        ${aiChatStatusTypography}
        font-weight: 500;
        margin-left: 0px;
        margin-right: auto;
        color: ${unsafeCSSVarV2('icon/activated')};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .loading-dots {
        display: inline;
        margin-left: 2px;
        color: ${unsafeCSSVarV2('icon/activated')};
      }
    }
    .ai-tool-call-wrapper.shine {
      position: relative;
      overflow: hidden;
      user-select: none;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
        width: 80px;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          ${unsafeCSSVarV2('layer/background/primary')},
          transparent
        );
        animation: shine 1.8s infinite;
      }
    }

    @keyframes shine {
      0% {
        left: -80px;
      }
      100% {
        left: 100%;
      }
    }

    /* Blue-White theme: dark background for AI chat panel */
    html[data-theme='blue-white'] .ai-tool-call-wrapper {
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.15);
    }

    html[data-theme='blue-white'] .ai-tool-call-wrapper .ai-icon svg,
    html[data-theme='blue-white'] .ai-tool-call-wrapper .ai-tool-name,
    html[data-theme='blue-white'] .ai-tool-call-wrapper .loading-dots {
      color: #64b5f6;
    }

    html[data-theme='blue-white'] .ai-tool-call-wrapper.shine::after {
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.1),
        transparent
      );
    }
  `;

  @property({ attribute: false })
  accessor name: string = 'Tool calling';

  @property({ attribute: false })
  accessor icon: TemplateResult<1> = ToolIcon();

  // sl-vt9e.1.2: Stream state — stops animation when response is finished
  @property({ attribute: false })
  accessor streamState: 'generating' | 'finished' = 'generating';

  @state()
  private accessor dotsText = '.';

  private animationTimer?: number;

  override connectedCallback() {
    super.connectedCallback();
    this.startDotsAnimation();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopDotsAnimation();
  }

  override updated(changed: Map<string, unknown>) {
    super.updated(changed);
    // sl-vt9e.1.2: Stop animation when stream finishes
    if (changed.has('streamState') && this.streamState === 'finished') {
      this.stopDotsAnimation();
      this.dotsText = '';
    }
  }

  private startDotsAnimation() {
    if (this.streamState === 'finished') return;
    let dotCount = 1;
    this.animationTimer = window.setInterval(() => {
      dotCount = (dotCount % 3) + 1;
      this.dotsText = '.'.repeat(dotCount);
    }, 750);
  }

  private stopDotsAnimation() {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = undefined;
    }
  }

  protected override render() {
    const isRunning = this.streamState !== 'finished';
    return html`
      <div class="ai-tool-call-wrapper ${isRunning ? 'shine' : ''}">
        <div class="ai-tool-header">
          <div class="ai-icon">${this.icon}</div>
          <div class="ai-tool-name">
            ${this.name}${isRunning
              ? html`<span class="loading-dots">${this.dotsText}</span>`
              : nothing}
          </div>
        </div>
      </div>
    `;
  }
}
